import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Star, MessageCircle, Copy, Check } from "lucide-react";
import { CONTACT } from "../data/site";
import { useSEO } from "../lib/seo";

// Direct Google review link (via maps short link — opens business, tap "Write review")
const REVIEW_URL = CONTACT.mapLink;

// External QR generator (no dependency needed)
const qrSrc = (url, size = 480) =>
    `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(
        url
    )}&color=0A0A0C&bgcolor=F4EFE4&qzone=2&margin=10`;

export default function ReviewQR() {
    const [copied, setCopied] = useState(false);

    useSEO({
        title:
            "Yorum Bırak — Cegu Masaj Salonu Özlüce | Google'da Puan Verin",
        description:
            "Cegu Masaj Salonu deneyiminizi Google'da paylaşın. QR kodu okutun, tek dokunuşla puan ve yorum yazın.",
        canonical: "/yorum-birak",
    });

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
    }, []);

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(REVIEW_URL);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (e) {
            // fallback
            window.prompt("Bağlantıyı kopyalayın:", REVIEW_URL);
        }
    };

    return (
        <main
            data-testid="review-qr-page"
            className="min-h-screen bg-[#0a0a0c] pt-[104px] pb-16 print:pt-8 print:bg-white"
        >
            {/* Ambient gold glow */}
            <div className="absolute top-40 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-[#c9a24b]/8 blur-[140px] pointer-events-none print:hidden" />

            <div className="container-x max-w-3xl relative">
                <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-[13px] text-[#c9c2b3] hover:text-[#c9a24b] mb-6 print:hidden"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Ana Sayfa
                </Link>

                {/* Card */}
                <div className="relative bg-gradient-to-br from-[#14141a] via-[#0f0f11] to-[#14141a] rounded-3xl border border-[#c9a24b]/30 p-6 md:p-10 lg:p-14 print:bg-white print:border-black print:text-black">
                    {/* Corner ornaments */}
                    <span className="absolute top-0 left-0 w-12 h-px bg-[#c9a24b] print:hidden" />
                    <span className="absolute top-0 left-0 w-px h-12 bg-[#c9a24b] print:hidden" />
                    <span className="absolute bottom-0 right-0 w-12 h-px bg-[#c9a24b] print:hidden" />
                    <span className="absolute bottom-0 right-0 w-px h-12 bg-[#c9a24b] print:hidden" />

                    <div className="text-center">
                        {/* Logo + brand */}
                        <div className="inline-flex flex-col items-center">
                            <div className="w-16 h-16 rounded-full overflow-hidden bg-black ring-1 ring-[#c9a24b]/40 mb-4 print:ring-black">
                                <img
                                    src={CONTACT.logoUrl}
                                    alt="Cegu Logo"
                                    className="w-full h-full object-cover logo-blend print:filter-none print:mix-blend-normal"
                                />
                            </div>
                            <div className="eyebrow print:text-black">
                                Cegu Masaj · Bursa Özlüce
                            </div>
                        </div>

                        <h1 className="mt-5 text-[2rem] md:text-[2.5rem] font-bold text-[#f4efe4] leading-[1.1] tracking-tight print:text-black">
                            Deneyiminizi{" "}
                            <span className="gold-text print:text-black">
                                paylaşın
                            </span>
                        </h1>
                        <div className="divider-gold mt-5 mx-auto" />
                        <p className="mt-5 text-[14.5px] leading-relaxed text-[#b8b1a1] max-w-lg mx-auto print:text-black">
                            Cegu Masaj Salonu'ndaki deneyiminizi Google'da
                            paylaşarak diğer misafirlerin doğru yeri bulmasına
                            yardım edin. QR kodu telefon kameranızla okutmanız
                            yeterli.
                        </p>

                        {/* Stars visual */}
                        <div className="mt-6 inline-flex items-center gap-1">
                            {[...Array(5)].map((_, i) => (
                                <Star
                                    key={i}
                                    className="w-7 h-7 md:w-8 md:h-8 star drop-shadow-[0_2px_8px_rgba(245,214,133,0.35)] print:filter-none"
                                />
                            ))}
                        </div>

                        {/* QR Code */}
                        <div
                            className="mt-8 inline-block bg-[#f4efe4] p-4 rounded-2xl shadow-[0_20px_60px_-20px_rgba(201,162,75,0.35)] print:shadow-none print:p-2"
                            data-testid="qr-code"
                        >
                            <img
                                src={qrSrc(REVIEW_URL, 520)}
                                alt="Google Yorum QR Kodu"
                                className="w-[220px] h-[220px] md:w-[260px] md:h-[260px]"
                                width={260}
                                height={260}
                            />
                        </div>

                        <div className="mt-6 text-[12.5px] uppercase tracking-[0.24em] text-[#7d766a] print:text-black">
                            Telefon kamerasıyla okutun
                        </div>

                        {/* Or open link */}
                        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center print:hidden">
                            <a
                                href={REVIEW_URL}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="review-open-link"
                                className="btn btn-gold"
                            >
                                <Star className="w-4 h-4" fill="currentColor" />
                                Google'da Yorum Bırak
                            </a>
                            <button
                                onClick={copyLink}
                                data-testid="review-copy-link"
                                className="btn btn-ghost"
                            >
                                {copied ? (
                                    <>
                                        <Check className="w-4 h-4" />
                                        Kopyalandı
                                    </>
                                ) : (
                                    <>
                                        <Copy className="w-4 h-4" />
                                        Bağlantıyı Kopyala
                                    </>
                                )}
                            </button>
                        </div>

                        {/* Print + share tips */}
                        <div className="mt-10 pt-6 border-t border-dashed border-[#c9a24b]/30 grid sm:grid-cols-3 gap-4 text-left print:border-black">
                            {[
                                {
                                    n: "1",
                                    t: "Kamera Aç",
                                    d: "Telefon kamerasını QR koda tutun.",
                                },
                                {
                                    n: "2",
                                    t: "Bağlantıyı Aç",
                                    d: "Ekrandaki Google linkine dokunun.",
                                },
                                {
                                    n: "3",
                                    t: "Yorumunuzu Yazın",
                                    d: "Yıldız verip birkaç kelime yazın.",
                                },
                            ].map((s) => (
                                <div
                                    key={s.n}
                                    className="rounded-xl bg-[#0a0a0c]/60 border border-[#23232b] p-4 print:bg-white print:border-black"
                                >
                                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#f5d685] to-[#8a6a28] text-[#0a0a0c] flex items-center justify-center text-[12px] font-bold mb-2 print:bg-black print:text-white">
                                        {s.n}
                                    </div>
                                    <div className="text-[13.5px] font-semibold text-[#f4efe4] print:text-black">
                                        {s.t}
                                    </div>
                                    <div className="text-[12px] text-[#8a8380] mt-1 leading-snug print:text-black">
                                        {s.d}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mt-8 flex flex-wrap items-center justify-center gap-2 print:hidden">
                            <button
                                onClick={() => window.print()}
                                data-testid="review-print"
                                className="text-[12.5px] text-[#c9c2b3] hover:text-[#f5d685] underline underline-offset-4"
                            >
                                QR Sayfayı Yazdır
                            </button>
                            <span className="text-[#7d766a]">·</span>
                            <a
                                href={`https://wa.me/?text=${encodeURIComponent(
                                    `Cegu Masaj Salonu için Google'da yorum bırakabilirsiniz: ${REVIEW_URL}`
                                )}`}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="review-share-wa"
                                className="text-[12.5px] text-[#c9c2b3] hover:text-[#f5d685] underline underline-offset-4 inline-flex items-center gap-1"
                            >
                                <MessageCircle className="w-3 h-3" />
                                WhatsApp'ta Paylaş
                            </a>
                        </div>
                    </div>
                </div>

                {/* Footer info under card */}
                <p className="mt-6 text-center text-[11.5px] text-[#7d766a] print:text-black">
                    Cegu Masaj Salonu Özlüce · {CONTACT.phone} · Batı Palas,
                    Nilüfer / Bursa
                </p>
            </div>

            <style>{`
                @media print {
                    body, html { background: white !important; }
                    .print\\:hidden { display: none !important; }
                }
            `}</style>
        </main>
    );
}
